import { EventDateFilterType } from "client/Api/Hub/Events/eventViewer";
import {
	ReactElement,
	Ref,
	useMemo,
	useRef,
	useState
} from "react";
import { HubItemIsNewFilter } from "../../common";
import { localize } from "client_legacy/ndaw/culture";
import {
	ActionListDivider,
	ActionListHeader,
	ActionListItem,
	DropOverActionList,
	IconButton
} from "client/Components";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import styles from "./FilterMenu.module.less";

export type FilterMenuProps = {
	dateFilter: EventDateFilterType;
	isNewFilter: HubItemIsNewFilter;
	onIsNewFilterChange: (value: HubItemIsNewFilter) => void;
	onDateFilterChange: (value: EventDateFilterType) => void;
	trackingId: string;
};

export const FilterMenu = (props: FilterMenuProps): ReactElement => {
	const {
		dateFilter,
		isNewFilter,
		onDateFilterChange,
		onIsNewFilterChange,
		trackingId
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: Ref<HTMLButtonElement> = useRef<HTMLButtonElement>(null);

	const title = useMemo(
		(): string => {
			return `${localize(isNewFilterToLocalizationKey[isNewFilter])}, ${localize(dateFilterToLocalizationKey[dateFilter])}`;
		},
		[dateFilter, isNewFilter]
	);

	return (
		<div className={styles.root}>
			<IconButton
				icon="filter"
				kind="secondary"
				onClick={() => setIsOpen(x => !x)}
				trackingId={trackingId}
				ref={ref}
				size="large"
				tooltip={title}
			/>
			{isOpen && (
				<DropOverActionList
					target={ensureNotNil(ref.current)}
					onClose={() => setIsOpen(false)}
					isHeightConstrainted
					trackingId={generateTrackingId(trackingId, "__dropover")}
					targetAlignment="bl"
					contentAlignment="tl"
					menuTitle={localize("_FilterBy")}
				>
					<FilterMenuActions
						dateFilter={dateFilter}
						isNewFilter={isNewFilter}
						onDateFilterChange={onDateFilterChange}
						onIsReadStatusFilterChange={onIsNewFilterChange}
					/>
				</DropOverActionList>
			)}
		</div>
	);
};
FilterMenu.displayName = "FilterMenu";

type FilterMenuActionsProps = {
	dateFilter: EventDateFilterType;
	isNewFilter: HubItemIsNewFilter;
	onIsReadStatusFilterChange: (value: HubItemIsNewFilter) => void;
	onDateFilterChange: (value: EventDateFilterType) => void;
};

const FilterMenuActions = (props: FilterMenuActionsProps): ReactElement => {
	const {
		dateFilter,
		isNewFilter,
		onDateFilterChange,
		onIsReadStatusFilterChange
	} = props;

	const renderNewFilterItem = (value: HubItemIsNewFilter): ReactElement => {
		const isSelected: boolean = isNewFilter === value;

		return (
			<ActionListItem
				text={localize(isNewFilterToLocalizationKey[value])}
				isActive={isSelected}
				rightIcon={isSelected ? "check" : undefined}
				onClick={() => onIsReadStatusFilterChange(value)}
				trackingId={generateTrackingId("is-new-filter-item", `|${value}`)}
			/>
		);
	};

	const renderDateFilterItem = (value: EventDateFilterType): ReactElement => {
		const isSelected: boolean = dateFilter === value;

		return (
			<ActionListItem
				text={localize(dateFilterToLocalizationKey[value])}
				isActive={isSelected}
				rightIcon={isSelected ? "check" : undefined}
				onClick={() => onDateFilterChange(value)}
				trackingId={generateTrackingId("date-filter-item", `|${value}`)}
			/>
		);
	};

	return (
		<>
			<ActionListHeader
				text={localize("_FilterBy")}
			/>
			{renderNewFilterItem("all")}
			{renderNewFilterItem("new")}
			<ActionListDivider />
			{renderDateFilterItem(EventDateFilterType.Upcoming)}
			{renderDateFilterItem(EventDateFilterType.Past)}
		</>
	);
};
FilterMenuActions.displayName = "FilterMenuActions";

const isNewFilterToLocalizationKey: {[key in HubItemIsNewFilter]: LocalizationKey;} = {
	"all": "_All",
	"new": "_New",
};

const dateFilterToLocalizationKey: {[key in EventDateFilterType]: LocalizationKey;} = {
	[EventDateFilterType.Upcoming]: "_UpcomingEvents",
	[EventDateFilterType.Past]: "_PastEvents"
};